import { DUMMY_LOGGER } from 'UTILS/constants';
import { parseCandidate } from 'CHECKS/connectivity/candidate';

const DEFAULT_CANDIDATES_TIMEOUT = 1000;

/**
 * Based on webrtc-troubleshooter SymmetricNatTest (https://github.com/MyPureCloud/webrtc-troubleshooter/blob/master/src/diagnostics/SymmetricNatTest.ts)
 *
 * @param {Number} [candidatesTimeout]
 * @param {RTCConfiguration} [iceConfig]
 * @param {Boolean} useDataChannel
 * @param {{warn: function, debug: function, log: function, error: function, info: function}} [logger] - The logger to be used.
 * @returns {Promise<{nat: String, udp: Boolean, relay: Boolean}>}
 */
export const getNetworkStatus = async({
  candidatesTimeout = DEFAULT_CANDIDATES_TIMEOUT,
  iceConfig = {
    // 2 different public servers are needed to compare the NAT ports that are used.
    // If the ports are the same, then it's an asymmetric NAT (also called "full cone" NAT).
    // If the ports are different, then it's a symmetric NAT.
    iceServers: [
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
    ],
  },
  logger = DUMMY_LOGGER,
}) => {
  const candidates = {};
  let isSupportingUDP = false;
  let isSupportingRelay = false;
  let hasNoCandidate = true;
  let dataChannel = {};
  let pc = {};
  let timeoutId;

  logger.log('Nat - getNetworkStatus', { candidatesTimeout, iceConfig });

  const processCandidates = () => {
    logger.log('Nat - Process candidates', candidates);
    const relatedPorts = Object.keys(candidates);
    let natResult;
    if (hasNoCandidate) {
      natResult = 'none';
    } else if (relatedPorts.length === 1) {
      const relatedPort = relatedPorts[0];
      const ports = candidates[relatedPort];
      natResult = ports.length === 1 ? 'asymmetric' : 'symmetric';
    } else if (relatedPorts.length === 0) {
      natResult = 'noSrflx';
    } else {
      let hasAsymmetric = false;
      let hasSymmetric = false;
      // Using some() to break the loop early if we find both types of NAT
      relatedPorts.some((relatedPort) => {
        const ports = candidates[relatedPort];
        if (ports.length === 1) {
          hasAsymmetric = true;
        } else {
          hasSymmetric = true;
        }
        return hasAsymmetric && hasSymmetric;
      });
      if (hasSymmetric && !hasAsymmetric) {
        natResult = 'symmetric';
      } else if (!hasSymmetric && hasAsymmetric) {
        natResult = 'asymmetric';
      } else if (hasSymmetric && hasAsymmetric) {
        natResult = 'both'; // Probably cascading NATs.
      } else {
        natResult = 'noSrflx'; // Not able to detect NAT type.
      }
    }
    return {
      nat: natResult,
      udp: isSupportingUDP,
      relay: isSupportingRelay,
    };
  };

  try {
    return await new Promise(async(resolve, reject) => {
      try {
        pc = new RTCPeerConnection(iceConfig);
        // Prepare PC by adding tracks or dataChannel message
        dataChannel = pc.createDataChannel('symmetricNatTest');
        dataChannel.onopen = () => {
          dataChannel.send('Hi back!');
        };

        // Analyze candidates one by one and all together once they're all gathered
        pc.onicecandidate = (event) => {
          logger.log('Nat - onicecandidate', event);
          if (event.candidate && event.candidate.candidate) {
            hasNoCandidate = false;
            const candidate = parseCandidate(event.candidate.candidate);
            if (candidate.protocol === 'udp') {
              isSupportingUDP = true;
            }
            if (candidate.type === 'relay') {
              isSupportingRelay = true;
            }
            if (event.candidate && event.candidate.candidate.indexOf('srflx') !== -1) {
              if (!candidates[candidate['relatedPort']]) {
                candidates[candidate['relatedPort']] = [];
              }
              candidates[candidate['relatedPort']].push(candidate.port);
            }
          } else {
            // This is the end of candidates
            return resolve(processCandidates());
          }
        };

        pc.oniceconnectionstatechange = (e) => logger.log('Nat - oniceconnectionstatechange', pc.iceConnectionState, pc, e);
        pc.onicecandidateerror = (e) => logger.error('Nat - onicecandidateerror', e.errorCode, e.errorText, e, pc);
        pc.onicegatheringstatechange = (e) => logger.log('Nat - onicegatheringstatechange', pc.iceConnectionState, pc, e);

        // We do not wait for candidates forever
        timeoutId = setTimeout(() => {
          logger.log('Nat - Timeout reached, start processing candidates.');
          return resolve(processCandidates());
        }, candidatesTimeout);

        // Trigger the ICE
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
      } catch (error) {
        reject(error);
      }
    });
  } finally {
    if (typeof dataChannel?.close === 'function') {
      dataChannel.close();
    }
    if (typeof pc.close === 'function') {
      pc.close();
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};
